// The print gate: nothing leaves for the printer unless every page can be
// turned into PostScript (2026-10-07).
//
// A3 duplex sheets printed « [PDF to PS Conversion Error:] "A drawing error
// occurred." » on one side: the pages with progressive JPEGs (see
// printSafeJpeg.ts). Screen viewers never show it — only the PDF→PostScript
// step on the way to the printer does — so the file is checked HERE, before it
// is saved, and the export is refused with the page numbers when it fails.
//
// Checked on every page:
//   · every JPEG is one PostScript can decode (printSafeJpeg) — including
//     inside embedded pages (Form XObjects);
//   · the drawing instructions hold only real numbers (a NaN / Infinity /
//     1e+21 coordinate is valid JavaScript, invalid PDF, and a RIP error);
//   · the page is the size of the book (no stray page of another format);
//   · transparency is reported (warning only — Acrobat and RIPs flatten it).
//
// Pure pdf-lib, no browser API: runs in vitest. Guard: printPdfCheck.test.ts
import {
  decodePDFRawStream, PDFArray, PDFContentStream, PDFDict, PDFDocument, PDFName, PDFNumber,
  PDFRawStream, PDFRef, PDFStream,
} from 'pdf-lib';
import { jpegPrintIssue } from './printSafeJpeg';

export interface PrintProblem { page: number; what: string }
export interface PrintCheck { problems: PrintProblem[]; warnings: PrintProblem[]; pages: number }

const N = (s: string) => PDFName.of(s);
const latin1 = (b: Uint8Array) => { let s = ''; for (let i = 0; i < b.length; i += 1) s += String.fromCharCode(b[i]); return s; };

function streamBytes(s: PDFStream): Uint8Array {
  if (s instanceof PDFContentStream) return s.getUnencodedContents();
  if (s instanceof PDFRawStream) return decodePDFRawStream(s).decode();
  return new Uint8Array();
}

/** Numbers a PostScript interpreter cannot read, outside strings and names. */
export function badNumbers(content: string): string[] {
  const code = content
    .replace(/\((?:\\.|[^\\)])*\)/g, ' ')            // (literal strings)
    .replace(/<[0-9A-Fa-f\s]*>/g, ' ')                 // <hex strings>
    .replace(/\/[^\s/[\]()<>{}%]+/g, ' ');             // /Names
  const out = code.match(/(?<![\w.])-?(?:NaN|Infinity|\d+(?:\.\d+)?[eE][-+]?\d+)(?![\w.])/g) ?? [];
  return [...new Set(out)].slice(0, 5);
}

function filterNames(dict: PDFDict): string[] {
  const f = dict.lookup(N('Filter'));
  if (f instanceof PDFName) return [f.asString()];
  if (f instanceof PDFArray) return f.asArray().map((x) => (x instanceof PDFName ? x.asString() : String(x)));
  return [];
}

function checkResources(doc: PDFDocument, res: PDFDict | undefined, page: number, out: PrintCheck, seen: Set<string>, depth: number) {
  if (!res || depth > 4) return;
  const xobjs = res.lookup(N('XObject'));
  if (xobjs instanceof PDFDict) {
    for (const [name, value] of xobjs.entries()) {
      const key = value instanceof PDFRef ? value.toString() : '';
      const obj = value instanceof PDFRef ? doc.context.lookup(value) : value;
      if (!(obj instanceof PDFStream)) continue;
      const dict = obj.dict;
      const subtype = dict.lookup(N('Subtype'));
      if (subtype === N('Image')) {
        if (dict.has(N('SMask'))) out.warnings.push({ page, what: `${name.asString()}: image with transparency (flattened when printed)` });
        if (key && seen.has(key)) continue;
        if (key) seen.add(key);
        if (filterNames(dict).includes('/DCTDecode') && obj instanceof PDFRawStream) {
          const issue = jpegPrintIssue(obj.contents);
          if (issue) out.problems.push({ page, what: `${name.asString()}: ${issue}` });
        }
        const w = dict.lookup(N('Width')); const h = dict.lookup(N('Height'));
        if (!(w instanceof PDFNumber) || !(h instanceof PDFNumber) || w.asNumber() < 1 || h.asNumber() < 1) {
          out.problems.push({ page, what: `${name.asString()}: image without a valid size` });
        }
      } else if (subtype === N('Form')) {
        const inner = streamBytes(obj);
        const bad = badNumbers(latin1(inner));
        if (bad.length) out.problems.push({ page, what: `embedded page draws with invalid numbers (${bad.join(', ')})` });
        checkResources(doc, dict.lookup(N('Resources')) as PDFDict | undefined, page, out, seen, depth + 1);
      }
    }
  }
  const gs = res.lookup(N('ExtGState'));
  if (gs instanceof PDFDict) {
    for (const [name, value] of gs.entries()) {
      const g = value instanceof PDFRef ? doc.context.lookup(value) : value;
      if (!(g instanceof PDFDict)) continue;
      const ca = g.lookup(N('ca')); const CA = g.lookup(N('CA'));
      const alpha = Math.min(ca instanceof PDFNumber ? ca.asNumber() : 1, CA instanceof PDFNumber ? CA.asNumber() : 1);
      if (alpha < 1) out.warnings.push({ page, what: `${name.asString()}: ${Math.round(alpha * 100)}% opacity (flattened when printed)` });
    }
  }
}

/** Check every page of a print PDF (a loaded file, or a created one after `doc.flush()`).
 *  `pageSize` in points; null skips the size check. */
export function checkPrintPdf(doc: PDFDocument, pageSize: { width: number; height: number } | null): PrintCheck {
  const out: PrintCheck = { problems: [], warnings: [], pages: doc.getPageCount() };
  if (!out.pages) out.problems.push({ page: 0, what: 'the PDF has no page' });
  const seen = new Set<string>();
  doc.getPages().forEach((p, i) => {
    const page = i + 1;
    const box = p.getMediaBox();
    if (![box.x, box.y, box.width, box.height].every(Number.isFinite) || box.width <= 0 || box.height <= 0) {
      out.problems.push({ page, what: 'invalid page box' });
    } else if (pageSize) {
      // The book page, or the same page with its bleed (a few points larger).
      const okW = box.width >= pageSize.width - 0.5 && box.width <= pageSize.width + 40;
      const okH = box.height >= pageSize.height - 0.5 && box.height <= pageSize.height + 40;
      if (!okW || !okH) out.problems.push({ page, what: `page is ${box.width.toFixed(1)}×${box.height.toFixed(1)} pt, not the book size` });
    }
    const contents = p.node.Contents();
    const streams: PDFStream[] = [];
    if (contents instanceof PDFStream) streams.push(contents);
    else if (contents instanceof PDFArray) {
      for (const c of contents.asArray()) {
        const s = c instanceof PDFRef ? doc.context.lookup(c) : c;
        if (s instanceof PDFStream) streams.push(s);
      }
    }
    const bad = badNumbers(streams.map((s) => latin1(streamBytes(s))).join('\n'));
    if (bad.length) out.problems.push({ page, what: `draws with invalid numbers (${bad.join(', ')})` });
    checkResources(doc, p.node.Resources(), page, out, seen, 0);
  });
  return out;
}

export class PrintPdfError extends Error {
  readonly label: string;
  readonly problems: PrintProblem[];
  constructor(label: string, problems: PrintProblem[]) {
    super(
      `PDF ${label} refusé pour l'impression — ${problems.length} problème(s) : `
      + problems.slice(0, 5).map((p) => `page ${p.page} — ${p.what}`).join(' ; ')
      + (problems.length > 5 ? ' ; …' : '')
      + '. Rien n’a été envoyé.',
    );
    this.name = 'PrintPdfError';
    this.label = label;
    this.problems = problems;
  }
}

/**
 * Throws (the export is blocked) when a page could not be printed.
 *
 * ⚠️ FLUSH FIRST: pdf-lib writes embedded images into the file only at save
 * time — before `flush()` an image XObject is a reference to nothing, and the
 * check would pass a progressive JPEG it never saw.
 */
export async function assertPrintSafe(doc: PDFDocument, pageSize: { width: number; height: number } | null, label: string): Promise<PrintCheck> {
  await doc.flush();
  const r = checkPrintPdf(doc, pageSize);
  if (r.warnings.length) console.info(`[print-check] ${label}: ${r.warnings.length} transparency note(s)`, r.warnings.slice(0, 3));
  if (r.problems.length) throw new PrintPdfError(label, r.problems);
  return r;
}
